import { MachineUDTNumber } from '@/components/forms/machines/MachineUDTNumber'
import './ServiceDetails.css'

import React from 'react'
import { useCallback, useState, useRef, useEffect, useMemo, RefObject } from 'react'
import { Services } from '@/services/services'
import { useNavigate, useParams } from 'react-router-dom'

import { Translates } from '@/services/repository/i18n'
import { CurrentSession } from '@/services/repository/session'
import { MainPageLayout } from '@/components/layout/MainLayout'
import { useLogin } from '@/context/login'
import { Machine } from '@/services/model/machine'
import { useModal } from '@/components/modal/Modal'
import { formatDateOnly } from '@/common/time'
import { MachinePath } from '../Common'
import { BackButton } from '@/components/layout/NavBar'
import { MachinePopupDocBody } from '@/popups/docs/docs'
import { MachinePopupQRCodeBody } from '@/popups/qrcode/qrcode'
import { Roles, isAdminRole } from '@/services/repository/users'
import { SuperAdminEditMachinesPopup } from '@/pages/for/superadmin/machines/Machines'
import { MachineStatuses } from '../statuses'
import { anonCanAddFailure } from '@/env/config'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'
import { ServicemanEditMachineServiceFieldsPopup } from '@/popups/machines/ServiceFieldsPopup'


export const CommunalMachineServiceDetails = () => {
  const navigate = useNavigate()
  const { openModal } = useModal()
  const mid = useParams().mid
  const [machine, setMachine] = useState<Machine>()
  // const [translates, setTranslates] = useState<Translates>()
  const { services, session, translates } = useAppContext()

  const refreshData = () => {
    fetch(`/api/communal/machines/byid/${mid}`).then((response) => {
      return response.json()
    }).then((data) => {
      setMachine(data)
    })
  }

  useEffect(() => {
    refreshData()
  }, [mid])

  if (!machine || !translates) {
    return <Spinner />
  }

  return <MainPageLayout pageName='communal-dashboard-details' services={services} translates={translates} session={session} path={[...MachinePath({ session, translates, machine }), {
    title: translates.for(`page.machinedetails.title`)
  }]}
    navbarLeft={<BackButton services={services} translates={translates} fallbackUrl={`/communal/machine/${mid}`} />}>
    {/* <div className="container" style={{
      'backgroundImage': 'url("/public/img/machine/bgs/scissor-lift-background.PNG")',
      'backgroundSize':     '50%',
      'backgroundRepeat':   'no-repeat',
      'backgroundPosition': 'right center'
    }}> */}
    <div className="container">
      {machine.machineType?.manufacturer ? <div className='machine-logo-wrap'>
        {machine.machineType?.manufacturer}
        {/* <img src={`/public/img/machines/manufacture/${machine.machineType?.manufacturer}/logo.png`} /> */}
      </div> : null}

      {MachineStatuses(translates, machine)}

      <div className="grid">
        <div className="grid-item">
          <div className="label">{translates.for('forms.machine.fields.manufacturer.title')}</div>
          <div className="value">{machine.machineType?.manufacturer || '-'}</div>
        </div>
        <div className="grid-item">
          <div className="label">{translates.for('forms.machine.fields.model.title')}</div>
          <div className="value">{machine.machineType?.model || '-'}</div>
        </div>
        <div className="grid-item">
          <div className="label">{translates.for('forms.machine.fields.internalNumber.title')}</div>
          <div className="value">{machine.internalNumber || '-'}</div>
        </div>
        <div className="grid-item">
          <div className="label">{translates.for('forms.machine.fields.ucid.title')}</div>
          <div className="value">{machine?.params?.deviceData?.ucid || '-'}</div>
        </div> 
        { machine.lastUser ? <div className="grid-item">
          <div className="label">{translates.for('forms.machine.fields.user.title')}</div>
          <div className="value" onClick={() => {
            machine?.lastUser?.id && navigate(`/communal/statistics/users?user=${machine?.lastUser?.id}`)
          }}>{machine?.lastUser?.username || '-'}</div>
        </div> : null }
        <div className="grid-item">
          <div className="label">{translates.for('forms.machine.fields.soc.title')}</div>
          <div className="value">{machine?.params?.deviceData?.soc || '-'}</div>
        </div> 
        <div className="grid-item">
          <div className="label">{translates.for('forms.machine.fields.engineHours.title')}</div>
          <div className="value">{machine?.params?.deviceData?.engineHours || '-'}</div>
        </div> 
        <div className="grid-item">
          <div className="label">{translates.for('forms.machine.fields.ubat.title')}</div>
          <div className="value">{machine?.params?.deviceData?.ubat || '-'}</div>
        </div> 
        <div className="grid-item">
          <div className="label">{translates.for('forms.machine.fields.serialNumber.title')}</div>
          <div className="value">{machine.serialNumber || '-'}</div>
        </div>
        <MachineUDTNumber machineId={mid!} value={machine.udtNumber} services={services} session={session} translates={translates} onSaved={refreshData} />
        <div className="grid-item">
          <div className="label">{translates.for('forms.machine.fields.imei.title')}</div>
          <div className="value">{machine.imei || '-'}</div>
        </div>
        <div className="grid-item">
          <div className="label">ICCID</div>
          <div className="value">{machine.params?.deviceData?.iccid || '-'}</div>
        </div> 

        {/* Service fields */}
        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.dtc_errors')}</div>
          <div className="value">{machine.params?.deviceData?.dtcErrors != null ? machine.params.deviceData.dtcErrors.toFixed(0) : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.total_odometer')}</div>
          <div className="value">{machine.params?.deviceData?.totalOdometer != null ? (machine.params.deviceData.totalOdometer / 1000).toFixed(0) + ' km' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.engine_rpm')}</div>
          <div className="value">{machine.params?.deviceData?.engineRPM != null ? machine.params.deviceData.engineRPM.toFixed(0) + ' rpm' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.fuel_level')}</div>
          <div className="value">{machine.params?.deviceData?.fuelLevel != null ? machine.params.deviceData.fuelLevel.toFixed(0) + '%' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.engine_coolant_temperature')}</div>
          <div className="value">{machine.params?.deviceData?.engineCoolantTemperature != null ? machine.params.deviceData.engineCoolantTemperature.toFixed(0) + '°C' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.fuel_used')}</div>
          <div className="value">{machine.params?.deviceData?.fuelUsed != null ? machine.params.deviceData.fuelUsed.toFixed(0) + ' l' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.vehicle_speed')}</div>
          <div className="value">{machine.params?.deviceData?.vehicleSpeed != null ? machine.params.deviceData.vehicleSpeed.toFixed(0) + ' km/h' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.accelerator_pedal_position')}</div>
          <div className="value">{machine.params?.deviceData?.acceleratorPedalPosition != null ? machine.params.deviceData.acceleratorPedalPosition.toFixed(0) + '%' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.engine_load')}</div>
          <div className="value">{machine.params?.deviceData?.engineLoad != null ? machine.params.deviceData.engineLoad.toFixed(0) + '%' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.engine_oil_temperature')}</div>
          <div className="value">{machine.params?.deviceData?.engineOilTemperature != null ? machine.params.deviceData.engineOilTemperature.toFixed(0) + '°C' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.fuel_rate')}</div>
          <div className="value">{machine.params?.deviceData?.fuelRate != null ? machine.params.deviceData.fuelRate.toFixed(0) + ' l/h' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.engine_hours')}</div>
          <div className="value">{machine.params?.deviceData?.engineHours != null ? machine.params.deviceData.engineHours.toFixed(0) + ' h' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.device_battery_temperature')}</div>
          <div className="value">{machine.params?.deviceData?.deviceBatteryTemperature != null ? machine.params.deviceData.deviceBatteryTemperature.toFixed(0) + '°C' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.throttle_position')}</div>
          <div className="value">{machine.params?.deviceData?.throttlePosition != null ? machine.params.deviceData.throttlePosition.toFixed(0) + '%' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.fuel_rail_pressure')}</div>
          <div className="value">{machine.params?.deviceData?.fuelRailPressure != null ? machine.params.deviceData.fuelRailPressure.toFixed(0) + ' kPa' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.intake_air_temperature')}</div>
          <div className="value">{machine.params?.deviceData?.intakeAirTemperature != null ? machine.params.deviceData.intakeAirTemperature.toFixed(0) + '°C' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.boost_pressure')}</div>
          <div className="value">{machine.params?.deviceData?.boostPressure != null ? machine.params.deviceData.boostPressure.toFixed(0) + ' kPa' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.ambient_air_temperature')}</div>
          <div className="value">{machine.params?.deviceData?.ambientAirTemperature != null ? machine.params.deviceData.ambientAirTemperature.toFixed(0) + '°C' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.dpf_soot_level')}</div>
          <div className="value">{machine.params?.deviceData?.dpfSootLevel != null ? machine.params.deviceData.dpfSootLevel.toFixed(0) + '%' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.distance_since_dtc_clear')}</div>
          <div className="value">{machine.params?.deviceData?.distanceSinceDTCClear != null ? machine.params.deviceData.distanceSinceDTCClear.toFixed(0) + ' km' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.adblue_def_level')}</div>
          <div className="value">{machine.params?.deviceData?.adBlueDEFLevel != null ? machine.params.deviceData.adBlueDEFLevel.toFixed(0) + '%' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.oem_specific_parameter')}</div>
          <div className="value">{machine.params?.deviceData?.oemSpecificParameter != null ? machine.params.deviceData.oemSpecificParameter.toFixed(0) : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.mil_lamp_status')}</div>
          <div className="value">{machine.params?.deviceData?.milLampStatus != null ? machine.params.deviceData.milLampStatus.toFixed(0) : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.external_voltage')}</div>
          <div className="value">{machine.params?.deviceData?.externalVoltage != null ? (machine.params.deviceData.externalVoltage / 1000).toFixed(2) + ' V' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.battery_voltage')}</div>
          <div className="value">{machine.params?.deviceData?.batteryVoltage != null ? (machine.params.deviceData.batteryVoltage / 1000).toFixed(2) + ' V' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.battery_current')}</div>
          <div className="value">{machine.params?.deviceData?.batteryCurrent != null ? machine.params.deviceData.batteryCurrent.toFixed(0) + ' A' : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.gnss_status')}</div>
          <div className="value">{machine.params?.deviceData?.gnssStatus != null ? machine.params.deviceData.gnssStatus.toFixed(0) : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.mobile_connection_status')}</div>
          <div className="value">{machine.params?.deviceData?.mobileConnectionStatus != null ? machine.params.deviceData.mobileConnectionStatus.toFixed(0) : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.sleep_mode')}</div>
          <div className="value">{machine.params?.deviceData?.sleepMode != null ? machine.params.deviceData.sleepMode.toFixed(0) : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.gsm_cell_id')}</div>
          <div className="value">{machine.params?.deviceData?.gsmCellID != null ? machine.params.deviceData.gsmCellID.toFixed(0) : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.ignition_source')}</div>
          <div className="value">{machine.params?.deviceData?.ignitionSource != null ? machine.params.deviceData.ignitionSource.toFixed(0) : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.movement_source')}</div>
          <div className="value">{machine.params?.deviceData?.movementSource != null ? machine.params.deviceData.movementSource.toFixed(0) : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.active_gsm_operator_code')}</div>
          <div className="value">{machine.params?.deviceData?.activeGSMOperatorCode != null ? machine.params.deviceData.activeGSMOperatorCode.toFixed(0) : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.crash_detection')}</div>
          <div className="value">{machine.params?.deviceData?.crashDetection != null ? machine.params.deviceData.crashDetection.toFixed(0) : '-'}</div>
        </div>

        <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.unplug_detection')}</div>
          <div className="value">{machine.params?.deviceData?.unplugDetection != null ? machine.params.deviceData.unplugDetection.toFixed(0) : '-'}</div>
        </div>

        {/* <div className="grid-item">
          <div className="label">{translates.for('machine.servicefields.bluetooth_state')}</div>
          <div className="value">{machine.params?.deviceData?.bluetoothState != null ? machine.params.deviceData.bluetoothState.toFixed(0) : '-'}</div>
        </div> */}

      </div>

      <div className='btns'>
        {isAdminRole(session?.user.role) ? <a className='main-btn' onClick={() => { navigate(`/communal/machine/${mid}/failures`) }}>
          {translates.for('menus.communal.machine.failure_list')}
        </a> : null}
        {session?.user.role === Roles.Serviceman && mid ? <a className='main-btn' onClick={() => openModal((props) => ServicemanEditMachineServiceFieldsPopup({ ...props, machineId: mid, services, session, onSaved: refreshData }))}>
          {translates.for('elements.table.machines.menu.edit.edit_service_fields')}
        </a> : null}
      </div>

    </div>
  </MainPageLayout>
}
